import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { isFeatureEnabled } from '../../utils/featureFlags';

interface AuthModalProps {
  isVisible: boolean;
  onClose: () => void;
  initialMode?: 'signin' | 'signup' | 'forgot';
}

export const AuthModal: React.FC<AuthModalProps> = ({ 
  isVisible, 
  onClose, 
  initialMode = 'signup' 
}) => {
  const { t } = useTranslation();
  const { signUp, signIn, resetPassword, loading, error, clearError } = useAuth();
  const isPaywallEnabled = isFeatureEnabled('ENABLE_PAYWALL');
  
  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot'>(initialMode);
  
  // Update mode when initialMode prop changes
  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetSent, setResetSent] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isVisible) {
      setMode(initialMode);
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setResetSent(false);
      clearError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset when modal visibility changes
  }, [isVisible, initialMode]);

  // Memoized change handlers to prevent unnecessary re-renders
  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  }, []);

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  }, []);

  const handleConfirmPasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (mode === 'signup' && password !== confirmPassword) {
      return;
    }

    if (mode === 'signup') {
      await signUp(email, password);
    } else if (mode === 'forgot') {
      await resetPassword(email);
      setResetSent(true);
    } else {
      await signIn(email, password);
    }
    
    // Authentication was successful - the modal will be closed by the parent component
    // when the user state changes
  };

  const toggleMode = () => {
    if (mode === 'signin') {
      setMode('signup');
    } else if (mode === 'signup') {
      setMode('signin');
    } else {
      setMode('signin');
    }
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setResetSent(false);
    clearError();
  };

  const goToForgotPassword = () => {
    setMode('forgot');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setResetSent(false);
    clearError();
  };

  const backToSignIn = () => {
    setMode('signin');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setResetSent(false);
    clearError();
  };

  if (!isVisible) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {mode === 'signin' && t('signIn', { ns: 'auth' })}
            {mode === 'signup' && t('signUp', { ns: 'auth' })}
            {mode === 'forgot' && t('resetPassword', { ns: 'auth' })}
          </h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        
        {mode === 'signup' && (
          <div className="signup-message">
            {isPaywallEnabled ? (
              <>
                <h3>{t('trialMessage', { ns: 'auth' })}</h3>
                <p>{t('trialDescription', { ns: 'auth' })}</p>
                <div className="signup-benefits">
                  <h4>{t('signupBenefitsTitle', { ns: 'auth' })}</h4>
                  <ul>
                    <li>{t('benefitServerManagement', { ns: 'auth' })}</li>
                    <li>{t('benefitCategorization', { ns: 'auth' })}</li>
                    <li>{t('benefitUnlimitedConversions', { ns: 'auth' })}</li>
                    <li>{t('benefitPrioritySupport', { ns: 'auth' })}</li>
                  </ul>
                </div>
              </>
            ) : (
              <>
                <h3>{t('betaMessage', { ns: 'auth' })}</h3>
                <p>{t('betaDescription', { ns: 'auth' })}</p>
                <div className="signup-benefits">
                  <h4>{t('betaBenefitsTitle', { ns: 'auth' })}</h4>
                  <ul>
                    <li>{t('benefitServerManagement', { ns: 'auth' })}</li>
                    <li>{t('benefitCategorization', { ns: 'auth' })}</li>
                    <li>{t('benefitUnlimitedConversions', { ns: 'auth' })}</li>
                    <li>{t('benefitPrioritySupport', { ns: 'auth' })}</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        )}

        <div className="modal-body">
          {resetSent ? (
            <div className="auth-success">
              <p>{t('resetPasswordSent', { ns: 'auth' })}</p>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={backToSignIn}
              >
                {t('signIn', { ns: 'auth' })}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">{t('email', { ns: 'auth' })}</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder={t('emailPlaceholder', { ns: 'auth' })}
                  required
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              {mode !== 'forgot' && (
                <div className="form-group">
                  <label htmlFor="password">{t('password', { ns: 'auth' })}</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder={t('passwordPlaceholder', { ns: 'auth' })}
                    required
                    disabled={loading}
                    minLength={6}
                    autoComplete="current-password"
                  />
                </div>
              )}

              {mode === 'signup' && (
                <div className="form-group">
                  <label htmlFor="confirmPassword">{t('confirmPassword', { ns: 'auth' })}</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    placeholder={t('confirmPasswordPlaceholder', { ns: 'auth' })}
                    required
                    disabled={loading}
                    minLength={6}
                  />
                  {password && confirmPassword && password !== confirmPassword && (
                    <div className="error-message">{t('passwordsDoNotMatch', { ns: 'auth' })}</div>
                  )}
                </div>
              )}

              {error && (
                <div className="error-message">{error}</div>
              )}

              <button 
                type="submit" 
                className="btn btn-primary auth-submit"
                disabled={loading}
              >
                {mode === 'signin' ? t('signIn', { ns: 'auth' }) :
                 mode === 'signup' ? t('signUp', { ns: 'auth' }) :
                 t('resetPassword', { ns: 'auth' })}
              </button>
            </form>
          )}

        <div className="auth-footer">
          {mode === 'signin' && (
            <>
              <p>
                {t('noAccount', { ns: 'auth' })}
                <button 
                  type="button" 
                  className="btn-link" 
                  onClick={toggleMode}
                  disabled={loading}
                >
                  {t('signUp', { ns: 'auth' })}
                </button>
              </p>
              <p>
                <button 
                  type="button" 
                  className="btn-link" 
                  onClick={goToForgotPassword}
                  disabled={loading}
                >
                  {t('forgotPassword', { ns: 'auth' })}
                </button>
              </p>
            </>
          )}
          {mode === 'signup' && (
            <p>
              {t('haveAccount', { ns: 'auth' })}
              <button 
                type="button" 
                className="btn-link" 
                onClick={toggleMode}
                disabled={loading}
              >
                {t('signIn', { ns: 'auth' })}
              </button>
            </p>
          )}
          {mode === 'forgot' && (
            <p>
              <button 
                type="button" 
                className="btn-link" 
                onClick={backToSignIn}
                disabled={loading}
              >
                {t('signIn', { ns: 'auth' })}
              </button>
            </p>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}; 